// The Appwrite AuthSource. Everything it throws is an AuthError carrying a
// member of the closed AuthErrorCode union; the SDK's own messages are
// English-only, leak internals, and never reach a learner.

import { AuthError, type AuthErrorCode, type AuthSource, type AuthUser } from "./source.ts";

/**
 * SDK exception → our closed union.
 *
 * A Record over the Appwrite types we have seen, plus two fall-throughs that
 * are decisions rather than accidents: no type and no code means fetch itself
 * failed, which is `network`; a type we do not know is `unknown`, which is a
 * real catalog entry (auth.errUnknown) and not a runtime fallback.
 */
const BY_TYPE: Record<string, AuthErrorCode> = {
	user_already_exists: "emailTaken",
	user_email_already_exists: "emailTaken",
	user_invalid_token: "invalidToken",
	user_target_already_exists: "emailTaken",
	// MFA the app never offers: someone switched it on before attaching this address.
	user_more_factors_required: "accountLocked",
};

export function authCode(e: unknown): AuthErrorCode {
	if (typeof e !== "object" || e === null) return "unknown";
	// Safe: the guard above already narrowed e to a non-null object. This cast
	// only widens it to "maybe has these two optional properties" so they can
	// be read without knowing the real thrower's class (AppwriteException, or
	// anything else) — every use below re-checks each field's type before
	// trusting it.
	const { type, code } = e as { type?: unknown; code?: unknown };
	if (typeof type === "string" && BY_TYPE[type]) return BY_TYPE[type];
	// No usable type and no HTTP status: fetch never reached the server.
	if (!code || code === 0) return "network";
	// The HTTP status decides the rest, and the decision is about the SESSION,
	// not the operation: a rate limit and a server blip leave a perfectly valid
	// session behind, so treating them like a 401 signs a learner out over a
	// transient condition and destroys their remembered session with it.
	if (typeof code === "number" && code >= 500) return "transient";
	// Its own code rather than "transient", so sign-in can say "too many
	// attempts"; Root keeps the session on it just the same.
	if (code === 429 || type === "general_rate_limit_exceeded") return "rateLimited";
	if (code === 403) return "forbidden";
	return "unknown";
}

// Exported only for appwrite.test.ts, to cover the passthrough below directly.
export const wrap = async <T>(run: () => Promise<T>): Promise<T> => {
	try {
		return await run();
	} catch (e) {
		// An AuthError thrown from inside `run` (deleteAccount does this for a
		// non-200 execution) already carries the right code. Re-deriving it via
		// authCode() would read AuthError's own shape as if it were an SDK
		// exception — it has no `type` and a truthy string `code`, so authCode()
		// happens to land on "unknown" today, but that is a coincidence of its
		// fall-through order, not a guarantee. Pass it through unchanged instead.
		if (e instanceof AuthError) throw e;
		throw new AuthError(authCode(e));
	}
};

/**
 * Appwrite's OAuth failure redirect carries `error` as a JSON-encoded
 * exception. Its type and code go through the same table as every SDK error;
 * the message is English and never shown.
 */
export function failureCode(raw: string | null): AuthErrorCode {
	if (!raw) return "unknown";
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return "unknown";
	}
	if (typeof parsed !== "object" || parsed === null) return "unknown";
	const c = authCode(parsed);
	// A redirect back from Appwrite reached the server by definition.
	return c === "network" ? "unknown" : c;
}

const appwriteType = (e: unknown): unknown =>
	// Safe: narrowed to a non-null object first; `type` is only compared.
	typeof e === "object" && e !== null ? (e as { type?: unknown }).type : undefined;

const toUser = (m: { $id: string; email: string; emailVerification: boolean }): AuthUser => ({
	id: m.$id,
	email: m.email,
	verified: m.emailVerification,
});

export interface GuestAccount extends UserAccount {
	createAnonymousSession(): Promise<{ userId: string }>;
}

/**
 * The id comes from the session itself: a second request could fail after
 * the session exists, leaving a signed-in browser the app thinks is signed
 * out. And a session that already exists (the first response was lost on the
 * way back) is resumed rather than refused — Appwrite would answer every
 * retry with user_session_already_exists.
 */
export async function startGuestOn(acct: GuestAccount): Promise<AuthUser> {
	try {
		const s = await acct.createAnonymousSession();
		return { id: s.userId, email: "", verified: false };
	} catch (e) {
		if (appwriteType(e) !== "user_session_already_exists") throw e;
		return userFromOn(acct);
	}
}

/**
 * deleteAccount's non-200 decision, pulled out so a test can see it without a
 * live Function execution (the same reason authCode is exported for its own
 * test). Null means the execution succeeded; otherwise the code is always
 * "unknown" — a non-200 here is a real server-side failure, not "no
 * connection", and unlike an SDK exception an Execution response carries no
 * `type`/`code` for authCode() to read a different verdict from.
 */
export function deleteAccountError(responseStatusCode: number): AuthError | null {
	return responseStatusCode === 200 ? null : new AuthError("unknown");
}

interface CodeAccount extends UserAccount {
	createSession(p: { userId: string; secret: string }): Promise<unknown>;
	deleteSession(p: { sessionId: string }): Promise<unknown>;
}

/** A guest saving has a session open; Appwrite refuses a second, so it gives way. */
export async function verifyCodeOn(
	acct: CodeAccount,
	userId: string,
	code: string,
): Promise<AuthUser> {
	try {
		await acct.createSession({ userId, secret: code });
	} catch (e) {
		if (appwriteType(e) !== "user_session_already_exists") throw e;
		await acct.deleteSession({ sessionId: "current" });
		await acct.createSession({ userId, secret: code });
	}
	return userFromOn(acct);
}

export interface SecretStore {
	get(userId: string): string | null;
	set(userId: string, secret: string): void;
	delete(userId: string): void;
}

/**
 * The throwaway password updateEmail demands. Kept only because a second
 * attach must present it; with password sign-in off it opens nothing.
 */
export const localSecrets: SecretStore = {
	get(userId) {
		try {
			return globalThis.localStorage?.getItem(`aybuben:attach:${userId}`) ?? null;
		} catch {
			return null;
		}
	},
	set(userId, secret) {
		try {
			globalThis.localStorage?.setItem(`aybuben:attach:${userId}`, secret);
		} catch {
			/* a second attach will fail and say so */
		}
	},
	delete(userId) {
		try {
			globalThis.localStorage?.removeItem(`aybuben:attach:${userId}`);
		} catch {
			/* nothing to do */
		}
	},
};

const randomSecret = (): string => {
	const bytes = crypto.getRandomValues(new Uint8Array(24));
	return btoa(String.fromCharCode(...bytes)).replace(/[^A-Za-z0-9]/g, "x");
};

interface AttachAccount {
	get(): Promise<{ $id: string; email: string }>;
	updateEmail(p: { email: string; password: string }): Promise<unknown>;
	createEmailToken(p: { userId: string; email: string }): Promise<{ userId: string }>;
}

const sameEmail = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

export async function attachEmailOn(
	acct: AttachAccount,
	email: string,
	secrets: SecretStore,
	newId: () => string,
): Promise<{ userId: string }> {
	const me = await acct.get();
	// Already carried (a previous attach, or a password save from an older
	// build whose password this device never had): only the code is missing.
	if (!sameEmail(me.email, email)) {
		const secret = secrets.get(me.$id) ?? randomSecret();
		// Kept first: if the response is lost after Appwrite applied it, a retry
		// must present the same secret.
		secrets.set(me.$id, secret);
		try {
			await acct.updateEmail({ email, password: secret });
		} catch (e) {
			// Appwrite answers a bare bad request when the address belongs to an
			// account with no email target or to a Google identity.
			if (appwriteType(e) === "general_bad_request") throw new AuthError("emailTaken");
			throw e;
		}
	}
	// The email now belongs to this guest, so the token signs into its id.
	const token = await acct.createEmailToken({ userId: newId(), email });
	return { userId: token.userId };
}

interface Identity {
	$id: string;
	provider: string;
	providerUid: string;
	providerEmail: string;
}

export interface UserAccount {
	get(): Promise<{ $id: string; email: string; emailVerification: boolean }>;
	listIdentities(): Promise<{ identities: Identity[] }>;
}

const provenBy = (email: string, verified: boolean, identities: Identity[]): boolean =>
	email !== "" && (verified || identities.some((i) => sameEmail(i.providerEmail, email)));

/**
 * The user, with `verified` meaning proven: Appwrite's flag (set by a code)
 * or a Google identity with the same address — Appwrite never sets the flag
 * when Google signs into an account whose email was attached unverified.
 */
export async function userFromOn(acct: UserAccount): Promise<AuthUser> {
	const me = await acct.get();
	if (me.email === "" || me.emailVerification) return toUser(me);
	const { identities } = await acct.listIdentities();
	return { id: me.$id, email: me.email, verified: provenBy(me.email, false, identities) };
}

interface SecureAccount extends UserAccount {
	deleteIdentity(p: { identityId: string }): Promise<unknown>;
	listSessions(): Promise<{
		sessions: { $id: string; provider: string; providerUid: string; current: boolean }[];
	}>;
	deleteSession(p: { sessionId: string }): Promise<unknown>;
	updateEmail(p: { email: string; password: string }): Promise<unknown>;
}

/**
 * Anyone can attach someone else's email to a guest, keep that anonymous
 * session, and even link their own Google account to it (Appwrite allows it
 * while the Google address matches no other account). Once the owner proves
 * the email, none of that may survive: identities with any other address go,
 * with the sessions they opened, and so do anonymous sessions. The owner's
 * own sessions come from codes or a Google identity with their address.
 *
 * An unproven account holding a Google identity is the other case: the same
 * person attached an address here, then chose Google. With this device's
 * throwaway secret it takes the Google address, which is proven.
 *
 * Returns the user as it now stands, or null when anything failed; it never
 * rejects, and the next launch tries again.
 */
export async function secureAccountOn(
	acct: SecureAccount,
	secrets: SecretStore,
): Promise<AuthUser | null> {
	try {
		const me = await acct.get();
		let email = me.email;
		let { identities } = await acct.listIdentities();
		if (!provenBy(email, me.emailVerification, identities)) {
			const googleEmail = identities.find(
				(i) => i.provider === "google" && i.providerEmail,
			)?.providerEmail;
			const secret = secrets.get(me.$id);
			if (!googleEmail || !secret) return toUser(me);
			await acct.updateEmail({ email: googleEmail, password: secret });
			email = googleEmail;
		}
		const foreign = identities.filter((i) => !sameEmail(i.providerEmail, email));
		for (const i of foreign) await acct.deleteIdentity({ identityId: i.$id });
		identities = identities.filter((i) => !foreign.includes(i));
		const theirs = new Set(foreign.map((i) => `${i.provider}:${i.providerUid}`));
		const { sessions } = await acct.listSessions();
		for (const x of sessions)
			if (
				!x.current &&
				(x.provider === "anonymous" || theirs.has(`${x.provider}:${x.providerUid}`))
			)
				await acct.deleteSession({ sessionId: x.$id });
		// Proven, so nothing will attach again; a kept secret could only ever
		// become a working password if password sign-in were turned back on.
		secrets.delete(me.$id);
		return { id: me.$id, email, verified: true };
	} catch {
		return null;
	}
}

/** The account calls AppwriteAuth makes; shaped to fit both the web and the React Native SDK's Account. */
export interface AuthAccount extends GuestAccount, CodeAccount, SecureAccount {
	createEmailToken(p: { userId: string; email: string }): Promise<{ userId: string }>;
	deleteSession(p: { sessionId: string }): Promise<unknown>;
	createJWT(): Promise<{ jwt: string }>;
}

export interface FunctionsApi {
	createExecution(p: {
		functionId: string;
		body: string;
		async: boolean;
		headers: Record<string, string>;
	}): Promise<{ responseStatusCode: number }>;
}

export interface AuthDeps {
	account: AuthAccount;
	functions: FunctionsApi;
	/** A fresh user id for an email token (the SDK's `ID.unique()`). */
	newId: () => string;
	deleteFunctionId: string;
	/** Starts a browser OAuth redirect. The web's; native signs in with ID tokens instead (M2). */
	startOAuth: (provider: string, successUrl: string, failureUrl: string) => void;
}

export class AppwriteAuth implements AuthSource {
	constructor(private readonly deps: AuthDeps) {}

	async current(): Promise<AuthUser | null> {
		try {
			return await userFromOn(this.deps.account);
		} catch (e) {
			// The bootstrap's three outcomes. A 401 is signed out — the ordinary path, and it
			// must never reach the "something went wrong" screen. Anything else,
			// including a fetch that never landed, is a real failure and throws, so
			// Root can fall back to the remembered session.
			// Guarded the same way authCode() guards above: unlike authCode, this
			// cast used to run unchecked, so a thrown non-object (e.g. `throw
			// null`) would raise its own TypeError reading `.code` right inside the
			// handler whose only job is to classify the failure.
			const code = typeof e === "object" && e !== null ? (e as { code?: unknown }).code : undefined;
			if (code === 401) return null;
			// Same guard and same reasoning as `code` above; the value is re-checked
			// as a string before it is kept.
			const type = typeof e === "object" && e !== null ? (e as { type?: unknown }).type : undefined;
			const err = new AuthError(authCode(e), typeof type === "string" ? type : undefined);
			// authCode() collapses the raw pair into a code; in development the
			// pair itself is what tells an unanticipated failure apart.
			if (
				err.code !== "network" &&
				process.env.NODE_ENV !== "production" &&
				process.env.NODE_ENV !== "test"
			)
				console.warn("account.get() failed", { type: err.appwriteType, code });
			throw err;
		}
	}

	startGuest(): Promise<AuthUser> {
		return wrap(() => startGuestOn(this.deps.account));
	}

	sendCode(email: string): Promise<{ userId: string }> {
		return wrap(async () => {
			const token = await this.deps.account.createEmailToken({ userId: this.deps.newId(), email });
			return { userId: token.userId };
		});
	}

	verifyCode(userId: string, code: string): Promise<AuthUser> {
		return wrap(() => verifyCodeOn(this.deps.account, userId, code));
	}

	attachEmail(email: string): Promise<{ userId: string }> {
		return wrap(() => attachEmailOn(this.deps.account, email, localSecrets, this.deps.newId));
	}

	secureAccount(): Promise<AuthUser | null> {
		return secureAccountOn(this.deps.account, localSecrets);
	}

	/**
	 * Initiates the OAuth2 flow for a client-side (CSR) app.
	 *
	 * Uses `createOAuth2Session` which automatically redirects the browser to the
	 * OAuth provider. On success, Appwrite redirects back to `successUrl` with an
	 * active session cookie already set. The `AuthSuccessPage` then only needs to
	 * verify the session and navigate to the app.
	 *
	 * This method does not return — the browser navigates away.
	 */
	createOAuth2Session(provider: string, successUrl: string, failureUrl: string): Promise<void> {
		return wrap(async () => {
			this.deps.startOAuth(provider, successUrl, failureUrl);
		});
	}

	signOut(): Promise<void> {
		return wrap(async () => {
			await this.deps.account.deleteSession({ sessionId: "current" });
		});
	}

	deleteAccount(): Promise<void> {
		return wrap(async () => {
			// Identity is proven with a short-lived JWT the Function
			// verifies cryptographically (account.get() under the JWT), not
			// claimed via a header — a differently-cased x-appwrite-user-id was
			// shown to be forgeable. The JWT travels in a
			// header, not the body: NOTHING identifying is sent in the body — a
			// userId in the payload would still let any signed-in learner delete
			// any other, so that refusal is unchanged.
			const { jwt } = await this.deps.account.createJWT();
			const run = await this.deps.functions.createExecution({
				functionId: this.deps.deleteFunctionId,
				body: "",
				async: false,
				headers: { "x-delete-jwt": jwt },
			});
			const err = deleteAccountError(run.responseStatusCode);
			// wrap()'s AuthError passthrough above carries this out unchanged.
			if (err) throw err;
		});
	}
}
